import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = []

  connect() {
    this._onTrixInitialize = this._onTrixInitialize.bind(this)
    this._onEditorDblClick = this._onEditorDblClick.bind(this)
    this.editorElement = this.element.querySelector("trix-editor")
    if (!this.editorElement) return

    this.editorElement.addEventListener("trix-initialize", this._onTrixInitialize)
    if (this.editorElement.toolbarElement) {
      this.addTableButton(this.editorElement)
    }
    this.editorElement.addEventListener("dblclick", this._onEditorDblClick)
    this.selectedAttachment = null
    this._modalTableData = null
    this._modalElement = null
    this._dialogElement = null
  }

  disconnect() {
    if (this.editorElement) {
      this.editorElement.removeEventListener("trix-initialize", this._onTrixInitialize)
      this.editorElement.removeEventListener("dblclick", this._onEditorDblClick)
    }
    this.destroyModal()
  }

  _onTrixInitialize() {
    this.addTableButton(this.editorElement)
  }

  addTableButton(editor) {
    const toolbar = editor.toolbarElement
    if (!toolbar || toolbar.querySelector("[data-trix-table-button]")) return

    const button = document.createElement("button")
    button.type = "button"
    button.innerText = "Table"
    button.className = "trix-button trix-button--icon trix-button--icon-table"
    button.setAttribute("data-trix-table-button", "")
    button.addEventListener("click", () => this.insertTable(editor))
    toolbar.querySelector(".trix-button-group--file-tools")?.appendChild(button)
  }

  insertTable(editor) {
    const tableHTML = this.buildTableHTML([
      ["ячейка 1", "ячейка 2"],
      ["ячейка 3", "ячейка 4"]
    ])
    const attachment = new Trix.Attachment({
      content: tableHTML,
      contentType: "application/vnd.trix.table.html"
    })
    editor.editor.insertAttachment(attachment)
  }

  _onEditorDblClick(event) {
    const attachmentElement = event.target.closest("[data-trix-attachment]")
    if (attachmentElement) {
      const attachment = this.findAttachmentByElement(this.editorElement, attachmentElement)
      if (
        attachment &&
        attachment.getContentType() === "application/vnd.trix.table.html"
      ) {
        this.selectedAttachment = attachment
        this.openModalWithTableFromAttachment(attachment)
      }
    }
  }

  openModalWithTableFromAttachment(attachment) {
    const html = attachment.getContent() || attachment.attachmentNode?.innerHTML
    const data = this.parseTableHTML(html)
    this._modalTableData = data
    this.createModal()
    this.renderModalTable()
  }

  createModal() {
    this.destroyModal()
    const dialog = document.createElement("dialog")
    dialog.className = "backdrop:bg-neutral-950/80 fixed inset-0 flex items-center justify-center min-h-screen w-screen h-screen bg-transparent border-0 p-0 m-0"
    dialog.style.maxWidth = "none"
    dialog.style.margin = "0"
    dialog.innerHTML = `
      <div class="bg-white rounded shadow-lg p-6 min-w-[300px]">
        <h2 class="text-lg font-bold mb-4">Edit Table</h2>
        <form>
          <div class="modal-content"></div>
          <div class="flex justify-end gap-2 mt-4">
            <button type="button" class="px-3 py-1 bg-gray-200 rounded" data-action="cancel">Cancel</button>
            <button type="submit" class="px-3 py-1 bg-blue-600 text-white rounded">Save</button>
          </div>
        </form>
      </div>
    `
    document.body.appendChild(dialog)
    dialog.showModal()
    this._modalElement = dialog
    this._dialogElement = dialog

    dialog.querySelector("form").addEventListener("submit", (e) => this.saveModal(e))
    dialog.querySelector("[data-action='cancel']").addEventListener("click", () => this.closeModal())
    dialog.querySelector(".modal-content").addEventListener("click", (e) => {
      if (e.target.matches("[data-action='add-row']")) this.addRow()
      if (e.target.matches("[data-action='add-col']")) this.addCol()
      if (e.target.matches("[data-action='remove-row']")) this.removeRow(e)
      if (e.target.matches("[data-action='remove-col']")) this.removeCol(e)
    })
  }

  destroyModal() {
    if (this._modalElement) {
      this._modalElement.remove()
      this._modalElement = null
      this._dialogElement = null
    }
  }

  closeModal() {
    this.destroyModal()
    this.selectedAttachment = null
    this._modalTableData = null
  }

  saveModal(event) {
    event.preventDefault()
    const rows = Array.from(this._dialogElement.querySelectorAll("tbody tr"))
    const data = rows.map(row =>
      Array.from(row.querySelectorAll("input")).map(input => input.value)
    )
    const newHTML = this.buildTableHTML(data)
    this.replaceTableAttachment(this.editorElement, this.selectedAttachment, newHTML)
    this.closeModal()
  }

  renderModalTable() {
    let data = this._modalTableData
    let html = `<table class="text-sm"><thead><tr>`
    if (data[0]) {
      data[0].forEach((_, colIndex) => {
        html += `<th>
          <button type="button" class="text-xs text-red-600 text-[16px] font-bold" data-action="remove-col" data-col-index="${colIndex}">✕</button>
        </th>`
      })
      html += `<th></th></tr></thead>`
    }
    html += `<tbody>`
    data.forEach((row, rowIndex) => {
      html += "<tr>"
      row.forEach((cell, colIndex) => {
        html += `<td class="px-2 py-1">
          <input type="text" value="${cell}" class="border rounded px-1 py-0.5 w-full" />
        </td>`
      })
      html += `<td>
        <button type="button" class="text-xs text-red-600 text-[16px] font-bold" data-action="remove-row" data-row-index="${rowIndex}">✕</button>
      </td>`
      html += "</tr>"
    })
    html += "</tbody></table>"
    html += `
      <div class="flex gap-2 mb-2">
        <button type="button" class="px-2 py-1 bg-gray-200 rounded text-xs" data-action="add-row">+ строка</button>
        <button type="button" class="px-2 py-1 bg-gray-200 rounded text-xs" data-action="add-col">+ колонка</button>
      </div>
    `
    this._dialogElement.querySelector(".modal-content").innerHTML = html
  }

  addRow() {
    this.updateModalTableDataFromInputs()
    this._modalTableData.push(Array(this._modalTableData[0]?.length || 2).fill(""))
    this.renderModalTable()
  }

  addCol() {
    this.updateModalTableDataFromInputs()
    this._modalTableData.forEach(row => row.push(""))
    this.renderModalTable()
  }

  removeRow(event) {
    this.updateModalTableDataFromInputs()
    const rowIndex = parseInt(event.target.getAttribute("data-row-index"), 10)
    if (this._modalTableData.length > 1) {
      this._modalTableData.splice(rowIndex, 1)
      this.renderModalTable()
    }
  }

  removeCol(event) {
    this.updateModalTableDataFromInputs()
    const colIndex = parseInt(event.target.getAttribute("data-col-index"), 10)
    if (this._modalTableData[0].length > 1) {
      this._modalTableData.forEach(row => row.splice(colIndex, 1))
      this.renderModalTable()
    }
  }

  buildTableHTML(data) {
    return `
      <div class="overflow-x-auto">
        <table class="w-max text-sm">
          <tbody>
            ${data.map(row =>
              `<tr>${row.map(cell =>
                `<td class="px-3 py-2">${cell}</td>`
              ).join("")}</tr>`
            ).join("")}
          </tbody>
        </table>
      </div>
    `
  }

  parseTableHTML(html) {
    const doc = new DOMParser().parseFromString(html, "text/html")
    const rows = Array.from(doc.querySelectorAll("tr"))
    return rows.map(row =>
      Array.from(row.querySelectorAll("td")).map(td => td.textContent)
    )
  }

  findAttachmentByElement(editor, element) {
    const attachments = editor.editor.getDocument().getAttachments()
    for (const att of attachments) {
      if (att.getContent() && element.innerHTML.includes(att.getContent().trim())) {
        return att
      }
    }
    return null
  }

  replaceTableAttachment(editor, attachment, newHTML) {
    const document = editor.editor.getDocument()
    const pieces = document.getPieces()
    for (let i = 0; i < pieces.length; i++) {
      const piece = pieces[i]
      if (piece.attachment === attachment) {
        editor.editor.setSelectedRange([piece.position, piece.position + 1])
        editor.editor.deleteInDirection("forward")
        const newAttachment = new Trix.Attachment({
          content: newHTML,
          contentType: "application/vnd.trix.table.html"
        })
        editor.editor.insertAttachment(newAttachment)
        break
      }
    }
  }

  updateModalTableDataFromInputs() {
    const rows = Array.from(this._dialogElement.querySelectorAll("tbody tr"))
    this._modalTableData = rows.map(row =>
      Array.from(row.querySelectorAll("input")).map(input => input.value)
    )
  }
}
